import { useState, useMemo, useCallback } from "react";
import { DistinctValue } from "../../../../types";
import {
  moveValues as apiMoveValues,
  EntityFieldItem,
} from "../../../../services/cleaning-data-service";
import { useDialogs } from "../../../../../dialog/use-dialog";
import { useNotification } from "../../../../../../context/notification-provider";

interface UseMoveValuesParams {
  fieldId: number | undefined;
  checked: { [key: string]: boolean };
  mainValues: DistinctValue[];
  selectedItem: DistinctValue | null;
  refreshDistinctValues: () => void;
  availableFields: EntityFieldItem[] | undefined;
}

interface UseMoveValuesReturn {
  selectedTargetField: EntityFieldItem | null;
  targetFieldOptions: EntityFieldItem[] | undefined;
  message: string;
  setSelectedTargetField: (field: EntityFieldItem | null) => void;
  handleMove: () => Promise<void>;
}

export default function useMoveValues({
  fieldId,
  checked,
  mainValues,
  selectedItem,
  refreshDistinctValues,
  availableFields,
}: UseMoveValuesParams): UseMoveValuesReturn {
  const { confirm } = useDialogs();
  const { showNotification } = useNotification();
  const [selectedTargetField, setSelectedTargetField] =
    useState<EntityFieldItem | null>(null);

  // Filter out the current field from available options
  const targetFieldOptions = useMemo(
    () => availableFields?.filter((field) => field.id !== fieldId),
    [availableFields, fieldId],
  );

  // Determine the message to display
  const message = useMemo(() => {
    const checkedCount = Object.values(checked).filter(Boolean).length;
    const totalCount = mainValues.length;

    if (!targetFieldOptions || targetFieldOptions.length === 0) {
      return "No other fields available to move values to.";
    }
    if (checkedCount > 0) {
      return `Move ${checkedCount} selected value(s) to a different field:`;
    }
    if (selectedItem) {
      return `Move "${selectedItem.value}" to a different field:`;
    }
    return `Move all ${totalCount} value${totalCount === 1 ? "" : "s"} to a different field:`;
  }, [checked, selectedItem, targetFieldOptions, mainValues]);

  const handleMove = useCallback(async () => {
    if (fieldId === undefined) {
      showNotification({
        message: "No field selected.",
        severity: "error",
        duration: 3000,
      });
      return;
    }

    if (!selectedTargetField) {
      showNotification({
        message: "Please select a target field.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    // Get values to move: prioritize checked items, then fall back to selected item, then all values
    let valuesToMove: string[] = [];
    const checkedValues = mainValues
      .filter((val) => checked[val.id])
      .map((val) => val.value);

    if (checkedValues.length > 0) {
      valuesToMove = checkedValues;
    } else if (selectedItem) {
      valuesToMove = [selectedItem.value];
    } else {
      // If nothing selected, move all values in the list
      valuesToMove = mainValues.map((val) => val.value);
    }

    if (valuesToMove.length === 0) {
      showNotification({
        message: "No values available to move.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    const targetField = selectedTargetField;

    try {
      const result = await confirm(
        `Are you sure you want to move ${valuesToMove.length} value${valuesToMove.length > 1 ? "s" : ""} to field "${targetField.name}"?`,
        {
          title: "Confirm Move Values",
          okText: "Move",
          cancelText: "Cancel",
          severity: "success",
        },
      );

      if (result === true) {
        try {
          await apiMoveValues(fieldId, targetField.id, valuesToMove);
          refreshDistinctValues();
          showNotification({
            message: `${valuesToMove.length} value${valuesToMove.length > 1 ? "s" : ""} moved successfully.`,
            severity: "success",
            duration: 3000,
          });
          setSelectedTargetField(null);
        } catch (err) {
          showNotification({
            message: `Failed to move values. Reason: ${(err as Error).message}`,
            severity: "error",
            duration: 3000,
          });
        }
      }
    } catch {
      showNotification({
        message: "Failed to show confirmation dialog.",
        severity: "error",
        duration: 3000,
      });
    }
  }, [
    fieldId,
    selectedTargetField,
    mainValues,
    checked,
    selectedItem,
    showNotification,
    confirm,
    refreshDistinctValues,
  ]);

  return {
    selectedTargetField,
    targetFieldOptions,
    message,
    setSelectedTargetField,
    handleMove,
  };
}
